import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-hot-toast";

interface TeamOption {
  id: string;
  name: string;
  region: {
    id: string;
    name: string;
  };
}

interface CustomerAssignment {
  id: string;
  displayName: string;
  teamId: string | null;
  queueKeys: string[];
}

interface AssignCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  customer: CustomerAssignment | null;
  teams: TeamOption[];
}

export function AssignCustomerModal({
  isOpen,
  onClose,
  onSuccess,
  customer,
  teams,
}: AssignCustomerModalProps) {
  const [formData, setFormData] = useState({ teamId: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && customer) {
      setFormData({ teamId: customer.teamId || "" });
    }
  }, [isOpen, customer]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!customer) {
      toast.error("No customer selected");
      return;
    }

    const nextTeamId = formData.teamId.trim();
    const wasAssigned = Boolean(customer.teamId);

    if (!nextTeamId && !wasAssigned) {
      toast.error("Please select a team");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch(`/api/v1/customer-profiles/${customer.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          teamId: nextTeamId || null,
        }),
      });

      if (response.ok) {
        const message = nextTeamId
          ? "Customer assigned successfully"
          : "Customer unassigned successfully";
        toast.success(message);
        onSuccess();
        onClose();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || "Failed to update customer assignment");
      }
    } catch (error) {
      console.error("Error updating customer assignment:", error);
      toast.error("Failed to update customer assignment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setFormData({ teamId: value });
  };

  if (!isOpen || !customer) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-lg font-medium text-gray-900">
              {customer.teamId ? "Update Customer Assignment" : "Assign Customer to Team"}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm font-medium text-gray-700">Customer</p>
              <p className="text-sm text-gray-900">{customer.displayName}</p>
              <p className="text-xs text-gray-500 mt-1">
                {customer.queueKeys.length} queue
                {customer.queueKeys.length === 1 ? "" : "s"}
              </p>
              {customer.queueKeys.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {customer.queueKeys.map((queueKey) => (
                    <span
                      key={`${customer.id}-${queueKey}`}
                      className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs text-gray-700"
                    >
                      {queueKey}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="teamId" className="block text-sm font-medium text-gray-700">
                Team
              </label>
              <select
                id="teamId"
                name="teamId"
                value={formData.teamId}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                disabled={isSubmitting}
              >
                <option value="">Unassigned</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name} ({team.region?.name})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
